import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import MerchantRegistration from '../../container/pages/merchantRegistration';
import { setSourcePackageId } from '../../redux/merchantRegistration/actions';
import { validateMerchantRegistrationLinkApi } from '../../redux/sagas/requests/merchantRegistrationRequest';

function parseSlugsFromPath(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const onboardingIndex = segments.indexOf('onboarding');
  if (onboardingIndex === -1 || onboardingIndex + 2 >= segments.length) {
    return { packageSlug: null, sourceSlug: null };
  }
  return {
    packageSlug: segments[onboardingIndex + 1],
    sourceSlug: segments[onboardingIndex + 2],
  };
}

function MerchantRegistrationGuard({ children }) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { packageSlug, sourceSlug } = useMemo(() => parseSlugsFromPath(pathname), [pathname]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'valid' | 'invalid'

  useEffect(() => {
    // 1. Immediate validation check
    if (!packageSlug || !sourceSlug) {
      setStatus('invalid');
      return;
    }

    let isMounted = true;
    setStatus('loading'); // Reset to loading if slugs change

    const validate = async () => {
      try {
        const response = await validateMerchantRegistrationLinkApi({
          package_slug: packageSlug.trim(),
          source_slug: sourceSlug.trim(),
        });

        // Check if the component is still mounted before updating state
        if (isMounted) {
          if (response?.data?.success || response?.data?.status === true) {
            const id = response?.data?.data?.source_package_id ?? response?.data?.source_package_id;
            dispatch(setSourcePackageId(id));
            setStatus('valid');
          } else {
            setStatus('invalid');
          }
        }
      } catch (error) {
        if (isMounted) setStatus('invalid');
      }
    };

    validate();

    return () => {
      isMounted = false;
    };
  }, [packageSlug, sourceSlug]);

  if (status === 'loading') {
    return (
      <div className="spin" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (status === 'invalid') {
    return <Navigate to="/invalid-slug" replace />;
  }

  return children ?? <MerchantRegistration />;
}

MerchantRegistrationGuard.propTypes = {
  children: PropTypes.node,
};

export default MerchantRegistrationGuard;
